import { useState } from 'react';
import apiService from '../services/api';
import MerchantOnboarding from './MerchantOnboarding';

/**
 * Adding a merchant Olive has not registered yet, so they can be onboarded now.
 *
 * Every other way into billing starts from the Olive snapshot, which means a
 * merchant we have signed but not yet added to Olive cannot be set up for ACH
 * until they appear there — often after their first transactions have already
 * come in. This is the way in that starts from a name.
 *
 * The name is not a label, it is the key: the server derives the merchant key
 * by normalizing the name, the same way it derives one for every merchant in
 * the snapshot. So a merchant added here under the name Olive will carry lands
 * on this same profile when their offers sync, and their transactions bill
 * against the authorization they gave in the meantime.
 *
 * Once the profile exists this hands straight off to MerchantOnboarding — the
 * same component every other merchant is set up through — so the link is
 * produced and copied here exactly the way it is everywhere else.
 */

const inputStyle = {
  flex: '1 1 200px',
  padding: '9px 12px',
  border: '1px solid #dee2e6',
  borderRadius: '8px',
  fontFamily: 'inherit',
  fontSize: '14px',
};

const MerchantInvite = ({ onAdded }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // The merchant this form has just created, and is now onboarding.
  const [added, setAdded] = useState(null);

  const ready = Boolean(name.trim() && email.trim());

  const add = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await apiService.saveBillingProfile({
        // No merchant_key: this merchant has no Olive row to take one from, so
        // the server derives it from the name.
        display_name: name.trim(),
        billing_email: email.trim(),
      });
      const profile = res?.profile;
      if (!profile?.merchant_key) {
        setError('The merchant was saved but came back without a key.');
        return;
      }
      setAdded({ profile, name: name.trim() });
      if (onAdded) onAdded(profile);
      setName('');
      setEmail('');
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not add that merchant.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        border: '1px solid #e9ecef',
        borderRadius: '12px',
        background: 'white',
        padding: '16px 20px',
        marginBottom: '16px',
      }}
    >
      <div style={{ fontWeight: 600, fontSize: '15px', marginBottom: '4px' }}>
        Add a merchant
      </div>
      <p
        className="staff-page-content"
        style={{ margin: '0 0 12px', fontSize: '13px', color: '#666' }}
      >
        For a merchant we have signed but Olive has not registered yet. Enter the
        name exactly as it will appear in Olive.
      </p>

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Merchant name"
          style={inputStyle}
        />
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Billing email at the merchant"
          style={inputStyle}
        />
        <button
          type="button"
          onClick={add}
          disabled={saving || !ready}
          style={{
            padding: '9px 16px',
            borderRadius: '8px',
            border: 'none',
            background: saving || !ready ? '#adb5bd' : '#0d6efd',
            color: 'white',
            fontFamily: 'inherit',
            fontSize: '14px',
            fontWeight: 500,
            cursor: saving || !ready ? 'not-allowed' : 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          {saving ? 'Adding…' : 'Add merchant'}
        </button>
      </div>

      {error && (
        <div style={{ marginTop: '10px', color: '#b00020', fontSize: '13px' }}>
          {error}
        </div>
      )}

      {added && (
        <div
          style={{
            marginTop: '14px',
            paddingTop: '14px',
            borderTop: '1px solid #f0f0f0',
          }}
        >
          <div style={{ fontSize: '13px', color: '#666', marginBottom: '10px' }}>
            <strong>{added.name}</strong> added. Create their onboarding link
            below and send it over — they also have a row in the list, so you can
            come back to them later.
          </div>
          <MerchantOnboarding
            merchantKey={added.profile.merchant_key}
            merchantName={added.name}
            profile={added.profile}
            onProfileSaved={(profile) => {
              if (!profile) return;
              setAdded((prev) => (prev ? { ...prev, profile } : prev));
              if (onAdded) onAdded(profile);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default MerchantInvite;
